import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Paginate } from '~/eds/src/components/Paginate/Paginate';
import { EntityType } from '~/enums/EntityType';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { MODEL_INSTRUCTIONS_ELEMENT_ID } from '~/features/x-ray/constants';
import { testIsTestCaseEvaluating } from '~/features/x-ray/utils/testcase';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useErrors } from '~/hooks/useErrors';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';
import { Uuid } from '~/types/Uuid';

import { PromptModelTestCase } from '../../../types/deprecated';
import { TableColumnSpecState } from '../../../types/table';
import { InstructionsEditor } from '../../Configuration/InstructionsEditor';
import { ModelScopeSelect } from '../../Configuration/ModelScopeSelect';
import { SaveAndEvaluateButton } from '../../Configuration/SaveAndEvaluateButton';
import { CHARACTER_LIMIT } from '../constants';
import { TableSpecConfiguration } from './TableSpecConfiguration';

interface Props {
  entityId: Nullable<string>;
  isConfigDirty: boolean;
  modelId: Nullable<Uuid>;
  readOnly: boolean;
  testCases: Nullable<PromptModelTestCase[]>;
}

export const TableConfiguration = ({
  entityId,
  isConfigDirty,
  modelId,
  readOnly,
  testCases,
}: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { RETRY_ERROR } = useErrors();
  const { navigate } = useRouting();
  const { toast } = useToast();

  const enableXrayNewPricingModel = useFlag(
    FlagType.DocumentXrayNewPricingModel,
  );
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);

  const isNew = !modelId;

  const versions = useSelector(slices.xRay.selectors.selectModelVersions);
  const activeVersionNumber = useSelector(
    slices.xRay.selectors.selectActiveVersionNumber,
  );

  const query = useSelector(slices.xRay.selectors.selectQuery);
  const activeQuery = isActiveQuery(query);
  const isScopeEmpty = enableXrayNewPricingModel && !activeQuery.length;

  const config = useSelector(slices.xRay.selectors.selectTableConfiguration);
  const { instructions = null, tableSpecState } = config;
  const { name, columns, key } = tableSpecState;

  const columnRequirementsMet =
    columns.length >= 1 &&
    columns.every(
      (column: TableColumnSpecState) =>
        column.name &&
        column.columnType &&
        column.description &&
        column.description.length <= CHARACTER_LIMIT,
    );
  const configRequirementsMet = Boolean(
    instructions && key && name && columnRequirementsMet,
  );

  const [saveDraft, { isLoading: isLoadingSaveDraft }] =
    api.endpoints.saveTableModelDraft.useMutation();

  const hasNotStartedOrErrorTestCases = (testCase: PromptModelTestCase) =>
    testCase.state === 'error' || testCase.state === 'not_started';

  const hasNoTestCases = testCases ? testCases.length === 0 : true;

  const hasTestCasesToEvaluate =
    testCases && !hasNoTestCases
      ? testCases.some(hasNotStartedOrErrorTestCases)
      : false;

  const hasTestCasesToRetry =
    testCases && !hasNoTestCases
      ? testCases.some(
          (testCase: PromptModelTestCase) => testCase.state === 'error',
        )
      : false;

  const hasEvaluatingTestCases =
    testCases && !hasNoTestCases
      ? testCases.some(testIsTestCaseEvaluating)
      : false;

  const isEvaluating = hasEvaluatingTestCases || isLoadingSaveDraft;

  const handleEvaluate = () => {
    saveDraft({
      modelId,
      config: {
        ...config,
        query: activeQuery,
      },
      highlightMethod,
    })
      .unwrap()
      .then((updatedModel) => {
        if (updatedModel) {
          if (isNew) {
            navigate(
              `${RoutePathType.AutomationHubFields}/${updatedModel.modelId}?type=table`,
            );
          } else {
            dispatch(
              slices.xRay.actions.setDismissedIncorrectStatusMessage(false),
            );
            dispatch(slices.xRay.actions.setReviewIncorrectCount(0));
          }
        }
      })
      .catch((error) => {
        const message = error.response?.data?.detail ?? RETRY_ERROR;
        if (
          message === 'No (more) documents were found in the model scope.' ||
          error.message === 'No (more) documents were found in the model scope.'
        ) {
          // toast will be rendered in the Configuration component
          if (isNew) {
            const newModelId = error.cause;
            if (newModelId) {
              navigate(
                `${RoutePathType.AutomationHubFields}/${newModelId}?type=table`,
              );
            }
          }
        } else if (message.includes('already exists')) {
          toast({
            message: intl.formatMessage({
              id: 'XRayTableConfiguration.saveAndEvaluate.duplicateTableKeyErrorMessage',
              defaultMessage:
                'The table key is already in use by another table model. Provide a unique table key.',
              description:
                'Error message when the provided table key is not unique.',
            }),
            status: 'danger',
          });
        } else {
          toast({
            message: intl.formatMessage({
              id: 'XRayTableConfiguration.saveAndEvaluate.noMoreScopeErrorMessage',
              defaultMessage:
                'No more documents were found in the model scope.',
              description:
                'Error message when no more documents are found in the model scope during save and evaluate in Custom AI configuration.',
            }),
            status: 'danger',
          });
        }
      });
  };

  const handlePageIndexUpdate = (pageIndex: number) => {
    dispatch(slices.xRay.actions.setDocumentViewer({}));
    dispatch(slices.xRay.actions.setActiveVersionNumber(pageIndex));
  };

  return (
    <Layout preset="form-fields">
      <TableSpecConfiguration
        isNew={!modelId}
        readOnly={readOnly}
        tableSpec={tableSpecState}
        onChange={(updatedTableSpec) =>
          dispatch(
            slices.xRay.actions.setTableConfiguration({
              ...config,
              tableSpecState: updatedTableSpec,
            }),
          )
        }
      />
      <ModelScopeSelect
        entityId={entityId}
        entityType={EntityType.DocumentAttachedData}
        readOnly={readOnly}
      />
      <FormField<string, false>
        description={intl.formatMessage({
          id: 'XRayTableConfiguration.instructions.modelDescriptionText',
          defaultMessage:
            'Tell the model what to do or let Evisort AI suggest instructions. You can also ask the model a question.',
          description:
            'Description for instructions field in Custom AI configuration of classification models.',
        })}
        input={InstructionsEditor}
        inputProps={{ readOnly }}
        label={intl.formatMessage({
          id: 'XRayTableConfiguration.instructions.label',
          defaultMessage: 'Instructions for the Model',
          description:
            'Label for instructions field in Custom AI table configuration.',
        })}
        name={MODEL_INSTRUCTIONS_ELEMENT_ID}
        placeholder={intl.formatMessage({
          id: 'XRayTableConfiguration.instructions.placeholder',
          defaultMessage: 'General instructions for the table model',
          description:
            'Placeholder for the instructions field in Custom AI table configuration',
        })}
        value={instructions}
        onChange={(updatedInstructions) =>
          dispatch(
            slices.xRay.actions.setTableConfiguration({
              ...config,
              instructions: updatedInstructions ?? undefined,
            }),
          )
        }
      />
      <Layout wrap align="center" justify="space-between" w="100%">
        {versions.length > 1 && (
          <Paginate
            disableGoTo
            mode="count"
            name="model-version-paginate"
            pageIndex={activeVersionNumber}
            totalCount={versions.length}
            onUpdatePageIndex={handlePageIndexUpdate}
          />
        )}
        <SaveAndEvaluateButton
          configRequirementsMet={configRequirementsMet && !isScopeEmpty}
          disabled={
            !configRequirementsMet ||
            (!isConfigDirty && !hasNoTestCases && !hasTestCasesToEvaluate) ||
            isScopeEmpty ||
            readOnly
          }
          hasNoChanges={!isConfigDirty && !hasNoTestCases}
          isEvaluating={isEvaluating}
          isLoading={isLoadingSaveDraft}
          readOnly={readOnly}
          shouldRetry={hasTestCasesToRetry}
          onClick={handleEvaluate}
        />
      </Layout>
    </Layout>
  );
};
